import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import toast from "../helpers/toast";
import constants, { siteurl } from "../../constants";
import RetailerHeader from "./RetailerHeader";
import RetailerSideMenu from "./RetailerSideMenu";
import Footer from "../patient/Patientfooter";
import { ToastContainer } from "react-toastify";
import SecureLS from "secure-ls";
import Httpconfig from "../helpers/HttpconfigRetailer";

var ls = new SecureLS({
  encodingType: "aes",
  encryptionSecret: "medleymed",
});

const TYPES = [
  { value: "offers", label: "Offers" },
  { value: "banners", label: "Banners" },
  { value: "categories", label: "Categories" },
];

const THEMES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "blue", label: "Blue" },
];

const CARD_TYPES = [
  { value: "default", label: "Default" },
  { value: "highlight", label: "Highlight" },
  { value: "compact", label: "Compact" }
];

// Guidance shown when the list for a type is empty
const EMPTY_GUIDANCE = {
  offers:     { msg: "No offers found.",     hint: "Create offers under Promotions > Offers first." },
  banners:    { msg: "No banners found.",    hint: "Create banners under Promotions > Banners first." },
  categories: { msg: "No categories found.", hint: "Create product categories first." },
};

export default function RetailerLayout() {
  const [rows, setRows] = useState([
    { type: "", theme: "light", card_type: "default", item_id: "" }
  ]);
  const originalRowsRef = useRef([]);
  const [offersList, setOffersList] = useState([]);
  const [bannersList, setBannersList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [entity_id, setEntityId] = useState(null);

  // Loading / empty state per type
  const [offersLoading, setOffersLoading]       = useState(false);
  const [bannersLoading, setBannersLoading]     = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [offersEmpty, setOffersEmpty]           = useState(false);
  const [bannersEmpty, setBannersEmpty]         = useState(false);
  const [categoriesEmpty, setCategoriesEmpty]   = useState(false);

  const offersFetchedRef     = useRef(false);
  const bannersFetchedRef    = useRef(false);
  const categoriesFetchedRef = useRef(false);

  // ── Load entity_id from session ───────────────────────────────────────────
  useEffect(() => {
    const userData = ls.get("retuserObj");
    if (userData && userData.retailer_id) {
      setEntityId(userData.retailer_id);
    }
  }, []);

  // ── Load existing layout rows ─────────────────────────────────────────────
  useEffect(() => {
    async function fetchRetailerLayouts() {
      if (!entity_id) return;
      try {
        const response = await Httpconfig.httptokenpost(
          constants.siteurl + "api/homelayout/gethomelayoutlist",
          { retailer_id: entity_id }
        );
        const result = response.data;
        if (result.success && Array.isArray(result.data)) {
          const loadedRows = result.data
            .sort((a, b) => a.position - b.position)
            .map(item => ({
              id: item.id,
              type: item.type || "",
              theme: item.theme || "light",
              card_type: item.card_type || "default",
              item_id: item.type_id || ""
            }));
          setRows(loadedRows.length ? loadedRows : [{ type: "", theme: "light", card_type: "default", item_id: "" }]);
          originalRowsRef.current = loadedRows;
        }
      } catch (err) {
        toast.error("Failed to fetch retailer layouts");
      }
    }
    fetchRetailerLayouts();
  }, [entity_id]);

  // ── Fetch lists when a matching type appears in rows ──────────────────────
  useEffect(() => {
    if (!entity_id) return;
    if (!offersFetchedRef.current && rows.some(row => row.type === "offers")) {
      fetchOffers();
      offersFetchedRef.current = true;
    }
    if (!bannersFetchedRef.current && rows.some(row => row.type === "banners")) {
      fetchBanners();
      bannersFetchedRef.current = true;
    }
    if (!categoriesFetchedRef.current && rows.some(row => row.type === "categories")) {
      fetchCategories();
      categoriesFetchedRef.current = true;
    }
  }, [rows, entity_id]);

  // After fetch: if list is empty, reset any rows of that type back to unselected
  const resetRowsOfType = (type) => {
    setRows(prev => prev.map(row =>
      row.type === type ? { ...row, type: "", item_id: "" } : row
    ));
  };

  const fetchOffers = async () => {
    setOffersLoading(true);
    try {
      const response = await Httpconfig.httptokenpost(
        constants.siteurl + "api/productsoffers/getofferslist",
        { entity_id: entity_id, page: 1, limit: 50 }
      );
      const result = response.data;
      if (result.status === 200 && Array.isArray(result.data) && result.data.length > 0) {
        setOffersList(result.data);
        setOffersEmpty(false);
      } else {
        setOffersEmpty(true);
        resetRowsOfType("offers");
        toast.warn("No offers available. Create offers under Promotions > Offers first.");
      }
    } catch (err) {
      toast.error("Failed to fetch offers");
      setOffersEmpty(true);
      resetRowsOfType("offers");
    } finally {
      setOffersLoading(false);
    }
  };

  const fetchBanners = async () => {
    setBannersLoading(true);
    try {
      const response = await Httpconfig.httptokenpost(
        constants.siteurl + "api/OM/Banners/list",
        { module_type: "", status: "", search: "", page: 1, limit: 50, entity_id: entity_id }
      );
      const result = response.data;
      if (result.status === 200 && Array.isArray(result.data) && result.data.length > 0) {
        setBannersList(result.data);
        setBannersEmpty(false);
      } else {
        setBannersEmpty(true);
        resetRowsOfType("banners");
        toast.warn("No banners available. Create banners under Promotions > Banners first.");
      }
    } catch (err) {
      toast.error("Failed to fetch banners");
      setBannersEmpty(true);
      resetRowsOfType("banners");
    } finally {
      setBannersLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await Httpconfig.httptokenpost(
        constants.siteurl + "api/productcategory/categorylist",
        { page: 1, limit: 10, entity_id: entity_id }
      );
      const result = response.data;
      if (Array.isArray(result.data) && result.data.length > 0) {
        setCategoriesList(result.data);
        setCategoriesEmpty(false);
      } else {
        setCategoriesEmpty(true);
        resetRowsOfType("categories");
        toast.warn("No categories available. Create product categories first.");
      }
    } catch (err) {
      toast.error("Failed to fetch categories");
      setCategoriesEmpty(true);
      resetRowsOfType("categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleRowChange = (idx, field, value) => {
    // If user picks a type already known to be empty, block it immediately
    if (field === "type" && value) {
      const emptyNow = { offers: offersEmpty, banners: bannersEmpty, categories: categoriesEmpty };
      if (emptyNow[value]) {
        const guide = EMPTY_GUIDANCE[value];
        toast.warn(`${guide.msg} ${guide.hint}`);
        return;
      }
    }

    const newRows = [...rows];
    newRows[idx][field] = value;
    if (field === "type") {
      newRows[idx].item_id = "";
      if (value === "offers")     offersFetchedRef.current = false;
      if (value === "banners")    bannersFetchedRef.current = false;
      if (value === "categories") categoriesFetchedRef.current = false;
    }
    setRows(newRows);
  };

  const handleItemChange = (idx, value) => {
    const newRows = [...rows];
    newRows[idx].item_id = value;
    setRows(newRows);
  };

  const addRow = () => {
    setRows([...rows, { type: "", theme: "light", card_type: "default", item_id: "" }]);
  };

  const deleteRow = (idx) => {
    const newRows = rows.filter((_, i) => i !== idx);
    setRows(newRows.length ? newRows : [{ type: "", theme: "light", card_type: "default", item_id: "" }]);
  };

  const handleSave = async () => {
    const isFreshCreate = rows.every(row => !row.id);

    // Rows with a type but no item selected are skipped with a warning
    const incompleteRows = rows.filter(row => row.type && !row.item_id);
    const validRows = rows.filter(row => row.type && row.item_id);

    if (incompleteRows.length > 0) {
      const skipped = [...new Set(incompleteRows.map(r => r.type))].join(", ");
      toast.warn(
        `${incompleteRows.length} row(s) skipped — no item selected for: ${skipped}. ` +
        `Please create the missing data or remove those rows.`
      );
    }

    if (validRows.length === 0) {
      toast.error("Nothing to save. Please select a type and item for at least one row.");
      return;
    }

    const payload = validRows.map((row, idx) => ({
      id: row.id || null,
      entity_id,
      type: row.type,
      theme: row.theme,
      card_type: row.card_type,
      type_id: row.item_id,
      position: idx + 1,
      status: 1,
      module_type: "ordermedince"
    }));

    try {
      const apiUrl = isFreshCreate
        ? constants.siteurl + "api/homelayout/"
        : constants.siteurl + "api/homelayout/update";
      const response = await Httpconfig.httptokenpost(apiUrl, payload);
      const data = response.data;
      if (data.success) {
        toast.success(isFreshCreate ? "Layouts created!" : "Layouts updated!");
      } else {
        toast.warn(isFreshCreate ? "Failed to create layouts!" : "Failed to update layouts!");
      }
    } catch (err) {
      toast.error("Error saving layouts!");
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
  };

  // Renders the item picker or an inline warning if the list is empty
  const renderItemPicker = (row, idx) => {
    if (!row.type) {
      return (
        <div style={{ color: "#888", fontStyle: "italic", padding: "8px 0" }}>
          Please select a type
        </div>
      );
    }

    const loadingMap = { offers: offersLoading, banners: bannersLoading, categories: categoriesLoading };
    const emptyMap   = { offers: offersEmpty,   banners: bannersEmpty,   categories: categoriesEmpty };
    const listMap    = { offers: offersList,     banners: bannersList,    categories: categoriesList };

    if (loadingMap[row.type]) {
      return <div style={{ color: "#888", padding: "8px 0" }}>Loading…</div>;
    }

    const list = listMap[row.type];

    if (row.type === "offers") {
      return (
        <select value={row.item_id} onChange={e => handleItemChange(idx, e.target.value)} className="form-control">
          <option value="">Select Offer</option>
          {list.map(offer => (
            <option key={offer.id} value={offer.id}>{offer.offer_name}</option>
          ))}
        </select>
      );
    }
    if (row.type === "banners") {
      return (
        <select value={row.item_id} onChange={e => handleItemChange(idx, e.target.value)} className="form-control">
          <option value="">Select Banner</option>
          {list.map(banner => (
            <option key={banner.banner_id} value={banner.banner_id}>{banner.banner_name}</option>
          ))}
        </select>
      );
    }
    if (row.type === "categories") {
      return (
        <select value={row.item_id} onChange={e => handleItemChange(idx, e.target.value)} className="form-control">
          <option value="">Select Category</option>
          {list.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
          ))}
        </select>
      );
    }
    return null;
  };

  return (
    <main id="main">
      <RetailerHeader />
      <RetailerSideMenu />
      <section id="Pharmacy_dashboard">
        <div className="container" style={{ maxWidth: 900, margin: "0px auto", paddingTop: "126px" }}>
          <div className="card" style={{ padding: "32px 24px", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ fontWeight: 600, margin: 0 }}>Layout Configuration</h3>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="rows">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {rows.map((row, idx) => (
                      <Draggable key={idx} draggableId={`row-${idx}`} index={idx}>
                        {(provided) => (
                          <div
                            className="form-row align-items-center"
                            style={{ marginBottom: 16, background: "#f8f9fa", borderRadius: 8 }}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <div style={{ cursor: "grab", marginRight: 8 }} {...provided.dragHandleProps}>
                              <span style={{ fontSize: 20 }}>☰</span>
                            </div>
                            <div className="col">
                              <select
                                value={row.type}
                                onChange={e => handleRowChange(idx, "type", e.target.value)}
                                className="form-control"
                              >
                                <option value="">Select Type</option>
                                {TYPES.map(t => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col">
                              {renderItemPicker(row, idx)}
                            </div>
                            <div className="col">
                              <select
                                value={row.theme}
                                onChange={e => handleRowChange(idx, "theme", e.target.value)}
                                className="form-control"
                              >
                                <option value="">Select Theme</option>
                                {THEMES.map(t => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col">
                              <select
                                value={row.card_type}
                                onChange={e => handleRowChange(idx, "card_type", e.target.value)}
                                className="form-control"
                              >
                                <option value="">Select Card Type</option>
                                {CARD_TYPES.map(c => (
                                  <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-auto" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <button
                                type="button"
                                onClick={addRow}
                                className="btn btn-primary"
                                title="Add Row"
                                style={{ borderRadius: "50%", width: 36, height: 36, fontSize: 20, padding: 0 }}
                              >+</button>
                              <button
                                type="button"
                                onClick={() => deleteRow(idx)}
                                className="btn btn-danger"
                                title="Delete Row"
                                style={{ borderRadius: "50%", width: 36, height: 36, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                              >
                                <span aria-hidden="true">&times;</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <div style={{ marginTop: 32, textAlign: "right" }}>
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-success"
                style={{ fontWeight: 600, padding: "10px 32px", fontSize: 16 }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
        <ToastContainer />
      </section>
    </main>
  );
}
